import { collectDocumentSlots, documentSlotValue, updateDocumentSlot } from './documentSlots';
import i18n from '../../i18n';
import { tableTitleWithOptions } from '../../shared/tables/tableDescriptors';
import {
  frozenFormProps, isListedInForm, isListedInTemplateForm, isVariableBox,
} from '../../shared/variables/variableBoxes';
import { isVariableTableBlock, WEB_TABLE_BLOCK_TYPE } from './webTableRefresh';
import {
  collectImageSlots, imageFormProp, sameSlot, slotBinding, slotForm, updateSlot,
} from './imageSlots';

/**
 * Box variables du site : ce que le formulaire peut lister.
 * - un bloc Texte (`text_variable` du classique) : le bloc lui-même, ses
 *   réglages dans `form` ;
 * - un tableau branché ou non (`table_variable` du classique) : le bloc
 *   lui-même, ses réglages dans `form` ;
 * - toute image d'un bloc (`img_variable`) : un emplacement d'image
 *   (`imageSlots`), ses réglages à côté de l'URL. Le bloc Image est le sien,
 *   comme la box image du classique.
 *
 * Une box se décrit par `{ block, boxKind, slot }` (`slot` nul pour un
 * texte ou un tableau) ; une entrée du formulaire en est une. La règle elle-même est
 * partagée avec le classique (`shared/variables/variableBoxes`).
 */

export const WEB_IMAGE_BLOCK_TYPE = 'ProposalImage';

export const WEB_TEXT_BLOCK_TYPE = 'ProposalRichText';

export const TABLE_FORM_PROP = 'form';

const IMAGE_BLOCK_SLOT = { path: '', urlProp: 'src', shape: 'flat' };

const imageBox = (block, slot) => ({ block, boxKind: 'image', slot });

/** La box qu'est le bloc lui-même (texte, image ou tableau), ou null. */
export function ownBox(block) {
  if (block?.type === WEB_IMAGE_BLOCK_TYPE) return imageBox(block, IMAGE_BLOCK_SLOT);
  if (block?.type === 'InfoCard') return { block, boxKind: 'infoCard', slot: null };
  if (block?.type === WEB_TEXT_BLOCK_TYPE) return { block, boxKind: 'text', slot: null };
  if (block?.type === WEB_TABLE_BLOCK_TYPE) return { block, boxKind: 'table', slot: null };
  return null;
}

// Masquer cache le bloc entier : seule la box qu'est le bloc le propose.
export const isOwnBox = (box) => !box.slot
  || (box.block.type === WEB_IMAGE_BLOCK_TYPE && sameSlot(box.slot, IMAGE_BLOCK_SLOT));

/** Les box possibles d'un bloc : lui-même, puis chaque image qu'il porte. */
export function blockBoxes(block) {
  const own = ownBox(block);
  const slots = collectImageSlots(block?.props)
    .filter((slot) => !own?.slot || !sameSlot(slot, own.slot));
  return [...(own ? [own] : []), ...slots.map((slot) => imageBox(block, slot)), ...collectDocumentSlots(block?.props).map((slot) => ({ block, slot, boxKind: 'document' }))];
}

/** `{ showInForm, name, remark, predefinedValues, valueRequired }`, vide tant que rien n'est réglé. */
export const boxSettings = ({ block, slot, boxKind }) => (
  boxKind === 'document' ? documentSlotValue(block.props, slot).form :
  slot ? slotForm(block.props, slot) : block.props?.[TABLE_FORM_PROP]
) || {};

const boundToCrm = ({ block, slot, boxKind }) => (
  boxKind === 'document' ? false :
  slot ? !!slotBinding(block.props, slot)?.variableKey : isVariableTableBlock(block)
);

const ruleInput = (box) => ({
  showInForm: boxSettings(box).showInForm,
  boundToCrm: boundToCrm(box),
  isVisible: box.block.props?.isVisible,
});

export const isSiteVariableBox = (box) => !!box && isVariableBox(ruleInput(box));

export const isSiteFormBox = (box) => !!box && isListedInForm(ruleInput(box));

export const isTemplateFormBox = (box) => !!box && isListedInTemplateForm(ruleInput(box));

export const hasVariableBox = (block) => blockBoxes(block).some(isSiteVariableBox);

/**
 * Les props avec les réglages de la box réécrits par `update`.
 *
 * @returns {object} les **mêmes props** si `update` rend les réglages tels quels
 */
export function updateBoxSettings(props, box, update) {
  const rewrite = (object, key) => {
    const current = object[key] || {};
    const next = update(current);
    return next === current ? object : { ...object, [key]: next };
  };
  if (box.boxKind === 'document') {
    return updateDocumentSlot(props, box.slot, (value) => rewrite(value, 'form'));
  }
  if (!box.slot) return rewrite(props, TABLE_FORM_PROP);
  return updateSlot(props, box.slot, (object) => rewrite(object, imageFormProp(box.slot.urlProp)));
}

/**
 * Ce qu'une écriture du formulaire fige sur la box (cf. frozenFormProps), dans
 * `props`, les props écrites. La règle se lit sur `before`, la box telle
 * qu'elle était : remplacer une image Smart4 la détache du CRM, et lue après
 * coup elle se figerait hors du formulaire.
 */
export function freezeBoxSettings(props, box, name, before = props) {
  const previous = { ...box, block: { ...box.block, props: before } };
  const frozen = frozenFormProps({ ...ruleInput(previous), name: boxSettings(previous).name }, name);
  if (!Object.keys(frozen).length) return props;
  return updateBoxSettings(props, box, (settings) => ({ ...settings, ...frozen }));
}

const BOX_TYPE_LABELS = { text: 'text_variable', image: 'img_variable', table: 'table_variable' };

export const boxTypeLabel = (box) => (box.boxKind === 'document' ? 'Document à télécharger' : box.boxKind === 'infoCard' ? 'Carte info' : i18n.t(BOX_TYPE_LABELS[box.boxKind]));

// Nom d'une box qui n'en a pas reçu : la variable liée, sinon le type — comme
// `mutateVariablesBoxes` du classique, avant numérotation.
export function boxSuggestedName(box) {
  const { block, slot } = box;
  if (box.boxKind === 'document') return documentSlotValue(block.props, slot).filename || boxTypeLabel(box);
  if (slot) return slotBinding(block.props, slot)?.variableName || boxTypeLabel(box);
  if (isVariableTableBlock(block)) {
    return tableTitleWithOptions(block.props.tableName, block.props.tableConfig, i18n.t.bind(i18n));
  }
  return boxTypeLabel(box);
}

export const boxBaseName = (box) => boxSettings(box).name?.trim() || boxSuggestedName(box);
